import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../translations/translations';

const HeroVariations = () => {
  const { language } = useLanguage();

  return (
    <div className="min-h-screen bg-white">
      {/* OPTION A: Editorial Bold */}
      <section className="min-h-screen bg-white py-32">
        <div className="max-w-6xl mx-auto px-8">
          <div className="space-y-12">
            {/* Small eyebrow */}
            <div className="border-l-2 border-black pl-6">
              <p className="text-xs font-medium tracking-widest uppercase text-gray-500">
                Legal incorporation services
              </p>
            </div>

            {/* Massive headline */}
            <div className="space-y-6">
              <h1 className="text-8xl font-light leading-none tracking-tight text-gray-900">
                Incorporate<br />in Mexico,<br />properly.
              </h1>
            </div>

            {/* Description in column */}
            <div className="max-w-xl space-y-8 border-l-2 border-gray-200 pl-8">
              <p className="text-xl leading-relaxed text-gray-700">
                A tailored, fully online incorporation service for founders, investors, 
                and business owners entering Mexico.
              </p>
              
              <p className="text-base leading-relaxed text-gray-600">
                Tell us what you want to set up in Mexico. We'll turn it into the right 
                legal structure and handle the process online.
              </p>

              {/* Minimal feature list */}
              <div className="space-y-4 pt-4">
                <div className="flex items-center space-x-3">
                  <div className="w-1 h-1 bg-black rounded-full"></div>
                  <span className="text-sm text-gray-600">Lawyer-led</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-1 h-1 bg-black rounded-full"></div>
                  <span className="text-sm text-gray-600">Notary-backed</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-1 h-1 bg-black rounded-full"></div>
                  <span className="text-sm text-gray-600">Fully online</span>
                </div>
              </div>

              {/* CTA */}
              <div className="pt-8">
                <Link to="/start">
                  <button className="bg-black text-white px-12 py-4 text-sm font-medium tracking-wide uppercase hover:bg-gray-900 transition-colors">
                    Start your incorporation
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Large number decoration */}
        <div className="absolute right-12 top-1/2 -translate-y-1/2 text-[12rem] font-light text-gray-100 pointer-events-none">
          01
        </div>
      </section>

      <div className="h-px bg-gray-900 my-16"></div>

      {/* OPTION B: Corporate Minimal */}
      <section className="min-h-screen bg-gray-50 py-32">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-12 gap-8">
            {/* Left column - Title */}
            <div className="col-span-7 space-y-16">
              <div>
                <p className="text-xs font-mono uppercase tracking-wider text-gray-400 mb-8">
                  BIMCORP MX
                </p>
                <h1 className="text-7xl font-light leading-tight text-gray-900">
                  Incorporate<br />in Mexico,<br />properly.
                </h1>
              </div>

              <div className="space-y-6 max-w-lg">
                <p className="text-lg text-gray-600 leading-relaxed">
                  A tailored, fully online incorporation service for founders, investors, 
                  and business owners entering Mexico.
                </p>
              </div>

              <div>
                <Link to="/start">
                  <button className="border-2 border-gray-900 text-gray-900 px-10 py-3 text-sm font-medium hover:bg-gray-900 hover:text-white transition-all">
                    START YOUR INCORPORATION
                  </button>
                </Link>
              </div>
            </div>

            {/* Right column - Stats/Features */}
            <div className="col-span-5 space-y-12 pt-32">
              <div className="space-y-8">
                <div className="border-t border-gray-300 pt-4">
                  <p className="text-xs font-mono uppercase tracking-wider text-gray-400 mb-2">
                    APPROACH
                  </p>
                  <p className="text-sm text-gray-700">Lawyer-led</p>
                </div>
                <div className="border-t border-gray-300 pt-4">
                  <p className="text-xs font-mono uppercase tracking-wider text-gray-400 mb-2">
                    FOUNDATION
                  </p>
                  <p className="text-sm text-gray-700">Notary-backed</p>
                </div>
                <div className="border-t border-gray-300 pt-4">
                  <p className="text-xs font-mono uppercase tracking-wider text-gray-400 mb-2">
                    DELIVERY
                  </p>
                  <p className="text-sm text-gray-700">Fully online</p>
                </div>
                <div className="border-t border-gray-300 pt-4">
                  <p className="text-xs font-mono uppercase tracking-wider text-gray-400 mb-2">
                    APPROACH
                  </p>
                  <p className="text-sm text-gray-700">Tailored for founders</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="h-px bg-gray-900 my-16"></div>

      {/* OPTION C: Editorial Refined */}
      <section className="min-h-screen bg-white py-32">
        <div className="max-w-6xl mx-auto px-8">
          <div className="grid grid-cols-2 gap-16 items-center">
            {/* Left - Content */}
            <div className="space-y-10">
              <div className="space-y-6">
                <h1 className="text-6xl font-semibold leading-tight text-gray-900 tracking-tight">
                  Incorporate in<br />Mexico, properly.
                </h1>
                <div className="w-16 h-0.5 bg-gray-900"></div>
              </div>

              <div className="space-y-6">
                <p className="text-lg text-gray-700 leading-relaxed">
                  A tailored, fully online incorporation service for founders, investors, 
                  and business owners entering Mexico.
                </p>
                
                <p className="text-base text-gray-600 leading-relaxed">
                  Tell us what you want to set up in Mexico. We'll turn it into the right 
                  legal structure and handle the process online.
                </p>
              </div>

              <div className="pt-4">
                <Link to="/start">
                  <button className="bg-gray-900 text-white px-10 py-4 text-sm font-medium hover:bg-gray-800 transition-colors">
                    Start your incorporation →
                  </button>
                </Link>
              </div>
            </div>

            {/* Right - Feature box */}
            <div className="bg-gray-50 p-12 border border-gray-200">
              <div className="space-y-8">
                <h3 className="text-sm font-medium uppercase tracking-wider text-gray-500">
                  What makes us different
                </h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Lawyer-led</h4>
                    <p className="text-sm text-gray-600">Expert legal guidance throughout</p>
                  </div>
                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Notary-backed</h4>
                    <p className="text-sm text-gray-600">Official notarial support included</p>
                  </div>
                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Fully online</h4>
                    <p className="text-sm text-gray-600">Complete process from anywhere</p>
                  </div>
                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Tailored approach</h4>
                    <p className="text-sm text-gray-600">Customized for your specific needs</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HeroVariations;
